import React, { useEffect, useMemo, useState } from "react";
import { Icon } from "semantic-ui-react";
import styles from "./PatternBox.module.scss";
import { useDispatch, useSelector } from "react-redux";
import { FormattedMessage, useIntl } from "react-intl";
import {
	Body1,
	Body3,
} from "../../../components/value-cssmodules/TextComponent";
import { addToList } from "../../../actions/cartActions";
import { removeFromList } from "../../../actions/cartActions";
import { setAlert } from "../../../actions/alertActions";
import { PatternData, PatternDifficulty } from "../../../types/siteTypes";
import { cartSelector, favouritesSelector } from "../../../utils/selectors";
import { Link, useNavigate } from "react-router-dom";
import {
	formatEur,
	getPatternPricingPresentation,
} from "../../../utils/patternPricing";
import { removePatternById } from "../../../actions/patternActions";
import SizeSelectionModal from "../../../components/SizeSelectionModal";
import {
	formatPatternSizeLabel,
	getOrderedPatternSizes,
	resolveCartSelectedSize,
} from "../../../utils/patternSize";

type Props = {
	pattern: PatternData;
	variant?: "buyer" | "owner";
};

const PatternBox: React.FC<Props> = ({
	pattern: {
		previewUrl,
		isEditable,
		sizes,
		difficulty,
		_id,
		name,
		photos,
		price,
		discount,
		omnibus,
		displayPricing,
		displayOmnibus,
	},
	variant = "buyer",
}) => {
	const intl = useIntl();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const cart = useSelector((state: any) => cartSelector(state));
	const favourites = useSelector((state: any) => favouritesSelector(state));
	const [isSizeSelectionOpen, setIsSizeSelectionOpen] = useState(false);
	const inCart = cart.filter((item: any) => item.id === _id).length > 0;
	const inFavourites = favourites.filter((item: any) => item.id === _id).length > 0;
	const orderedAvailableSizes = useMemo(
		() => getOrderedPatternSizes(Array.isArray(sizes) ? sizes : []),
		[sizes]
	);
	const selectedCartSizesForPattern = useMemo(
		() =>
			cart
				.filter((item: any) => item.id === _id)
				.map((item: any) => resolveCartSelectedSize(item))
				.filter(Boolean),
		[cart, _id]
	);
	const selectableSizes = useMemo(
		() =>
			orderedAvailableSizes.filter(
				(size) => !selectedCartSizesForPattern.includes(size)
			),
		[orderedAvailableSizes, selectedCartSizesForPattern]
	);
	const hasMultipleSizes = orderedAvailableSizes.length > 1;
	const normalizedDifficulty = typeof difficulty === "string" ? difficulty.trim().toLowerCase() : "";
	const normalizedPatternDifficulty: PatternDifficulty =
		normalizedDifficulty === "easy" ||
			normalizedDifficulty === "hard" ||
			normalizedDifficulty === "medium"
			? normalizedDifficulty
			: "medium";
	const difficultyLabel = intl.formatMessage({
		id: `pattern.difficulty.${normalizedPatternDifficulty}`,
		defaultMessage: normalizedPatternDifficulty,
	});

	const selectedSizes = orderedAvailableSizes.map(formatPatternSizeLabel);
	const maxVisibleSizes = 3;
	const hasMoreSizes = selectedSizes.length > maxVisibleSizes;
	const sizesText = hasMoreSizes
		? `${selectedSizes.slice(0, maxVisibleSizes).join(", ")}, ...`
		: selectedSizes.join(", ");
	const displayedSizesText = selectedSizes.length > 0 ? sizesText : "-";

	const handleDeletePattern = () => {
		const shouldDelete = window.confirm(
			intl.formatMessage({
				id: "pattern.removePattern.areYouSure",
				defaultMessage: "Would you really like to remove this pattern?",
			})
		);
		if (!shouldDelete) {
			return;
		}

		dispatch(removePatternById(_id, navigate, false));
	};

	useEffect(() => {
		window.scrollTo(0, 0);
	}, [_id]);

	const {
		showPromotionInfo,
		promotionBasePrice,
		currentEffectivePrice,
		lowestEffectivePrice,
		showOmnibusInfo,
		showOmnibusBadge,
		omnibusPercent,
	} = getPatternPricingPresentation({
		price,
		discount,
		displayPricing,
		omnibus,
		displayOmnibus,
	});
	const percent = showOmnibusBadge ? omnibusPercent : 0;
	const lowestPriceText =
		showOmnibusInfo && lowestEffectivePrice !== null
			? formatEur(lowestEffectivePrice)
			: null;

	const linkTarget = isEditable ? `/pattern/${_id}` : `/pattern/${_id}`;
	const previewImage = isEditable && previewUrl ? previewUrl : null;
	const modalImageUrl =
		(photos && photos.length > 0
			? photos[0].thumbnailUrl || photos[0].url
			: null) || "/photos/no-photo.jpg";
	const addPatternToCart = (selectedSizes?: string[]) => {
		const sizesToAdd =
			selectedSizes && selectedSizes.length > 0
				? selectedSizes
				: orderedAvailableSizes.length === 1
					? [orderedAvailableSizes[0]]
					: [];

		if (sizesToAdd.length === 0) {
			return;
		}

		dispatch(
			addToList(
				_id,
				"cart",
				sizesToAdd.length === 1 ? sizesToAdd[0] : sizesToAdd
			)
		);

		if (sizesToAdd.length === 1) {
			dispatch(
				setAlert(
					intl.formatMessage({
						id: "cart.addedToCart.single",
						defaultMessage: "Added to cart: {size}",
					}, {
						size: formatPatternSizeLabel(sizesToAdd[0]),
					}),
					"success"
				)
			);
			return;
		}
		dispatch(
			setAlert(
				intl.formatMessage({
					id: "cart.addedToCart.multiple",
					defaultMessage: "Added sizes to cart: {sizes}",
				}, {
					sizes: sizesToAdd
						.map((size) => formatPatternSizeLabel(size))
						.join(", "),
				}),
				"success"
			)
		);
	};

	return (
		<article className={styles.card}>
			<Link to={linkTarget} className={styles.mediaLink}>
				<div
					className={styles.media}
					style={{
						backgroundImage: previewImage
							? `url(${previewImage})`
							: `url(${photos && photos.length > 0
								? photos[0].url
								: "/photos/no-photo.jpg"
							})`,
						backgroundColor: isEditable ? "#f0f0f0" : "transparent",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
					}}
				>
					{isEditable && !previewImage && (!photos || photos.length === 0) && (
						<span style={{ color: "#666" }}>Draft Design</span>
					)}
					{showOmnibusBadge && <div className={styles.badge}>-{percent}%</div>}
				</div>
			</Link>

			<div
				className={`${styles.content} ${variant === "owner" ? styles.contentOwner : ""
					}`.trim()}
			>
				<div className={styles.meta}>
					<div className={styles.titleWrap}>
						<Link to={linkTarget} className={styles.titleLink}>
							<Body1 className={styles.title}>{name}</Body1>
						</Link>
					</div>
					<Body3 className={styles.metaLine}>
						<span className={styles.sizesMeta}>
							{displayedSizesText}
						</span>
						<span className={styles.difficultyMeta}>{difficultyLabel}</span>
					</Body3>
				</div>

				<footer className={styles.pricefooter}>
					{showPromotionInfo ? (
						<div className={styles.priceRow}>
							<span className={styles.oldPrice}>{formatEur(promotionBasePrice)}</span>
							<span className={styles.newPrice}>
								{formatEur(currentEffectivePrice)}
							</span>
						</div>
					) : (
						<span className={styles.regularPrice}>
							{formatEur(currentEffectivePrice)}
						</span>
					)}
					{showOmnibusInfo && lowestPriceText && (
						<span className={styles.omnibusLine}>
							<FormattedMessage
								id="cart.omnibusInfo"
								defaultMessage="Lowest price in 30 days before discount: {price}"
								values={{ price: lowestPriceText }}
							/>
						</span>
					)}
				</footer>
				{variant === "buyer" ? (
					<div className={styles.actions}>
						<Icon
							name={inFavourites ? "heart" : "heart outline"}
							size={"large"}
							className={inFavourites ? styles.activeActionIcon : ""}
							onClick={() => {
								if (inFavourites) {
									dispatch(removeFromList(_id, "favourites"));
									dispatch(
							setAlert(
								intl.formatMessage({
									id: "cart.removedFromFavourites",
									defaultMessage: "Removed from favourites",
								}),
								"info"
							)
						);
								} else {
									dispatch(addToList(_id, "favourites"));
									dispatch(
							setAlert(
								intl.formatMessage({
									id: "cart.addedToFavourites",
									defaultMessage: "Added to favourites",
								}),
								"success"
							)
						);
								}
							}}
						/>
						<Icon
							name="cart"
							size={"large"}
							className={inCart ? styles.activeActionIcon : ""}
							onClick={() => {
								if (hasMultipleSizes) {
									if (selectableSizes.length === 0) {
										dispatch(
									setAlert(
										intl.formatMessage({
											id: "cart.allSizesAlreadyInCart",
											defaultMessage: "All sizes of this pattern are already in cart",
										}),
										"info"
									)
								);
										navigate("/cart");
										return;
									}
									setIsSizeSelectionOpen(true);
									return;
								}

								if (inCart) {
									dispatch(
									setAlert(
										intl.formatMessage({
											id: "cart.alreadyInCart",
											defaultMessage: "This pattern is already in your cart",
										}),
										"info"
									)
								);
									navigate("/cart");
								} else {
									addPatternToCart();
								}
							}}
						/>
						<Icon
							name="share alternate"
							size={"large"}
							onClick={async () => {
								const patternUrl = `${window.location.origin}/pattern/${_id}`;
								try {
									await navigator.clipboard.writeText(patternUrl);
									dispatch(
								setAlert(
									intl.formatMessage({
										id: "cart.copyLinkSuccess",
										defaultMessage: "Copied the product link",
									}),
									"success"
								)
							);
								} catch (_error) {
									dispatch(
										setAlert(
											intl.formatMessage({
										id: "cart.copyLinkFailed",
										defaultMessage: "Failed to copy link. Please copy the address from your browser.",
									}),
											"warning",
										),
									);
								}
							}}
						/>
					</div>
				) : (
					<div className={styles.ownerActions}>
						<Link
							to={`/manage-pattern/${_id}`}
							className={`${styles.ownerCta} ${styles.ownerEditCta}`}
						>
							<FormattedMessage
								id="pattern.editPattern"
								defaultMessage="Edit pattern"
							/>
						</Link>
						<button
							type="button"
							onClick={handleDeletePattern}
							className={`${styles.ownerCta} ${styles.ownerDeleteCta}`}
						>
							<FormattedMessage
								id="pattern.removePattern"
								defaultMessage="Remove pattern"
							/>
						</button>
					</div>
				)}
			</div>
			{variant === "buyer" && (
				<SizeSelectionModal
					open={isSizeSelectionOpen}
					patternName={name}
					patternImageUrl={modalImageUrl}
					sizes={orderedAvailableSizes}
					unavailableSizes={selectedCartSizesForPattern}
					multiple={true}
					onClose={() => setIsSizeSelectionOpen(false)}
					onConfirm={(selectedSizes) => {
						addPatternToCart(selectedSizes);
						setIsSizeSelectionOpen(false);
					}}
				/>
			)}
		</article>
	);
};

export default PatternBox;
